import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Smart lists and saved filters (migration 074), the to-do page's side of
 * them: what the server has, what it says, and the changes to send it.
 *
 * Kept out of TodoApp.js, which hands this its `api` (the /todos routes, with
 * the same token and View-as) and the open to-dos, and draws what comes back.
 *
 *   filters   the person's saved filters, in their order, each with its count
 *             (null until asked; [] where 074 has not run)
 *   fields    the query fields this person may use on this server
 *   prefs     the sidebar's smart views as they arranged them, [{ key, hidden }]
 *   remote    the layouts and Display choices the server keeps for them
 *             ({ layouts, display, synced }), which follow them from device
 *             to device - `synced` false on a server that does not keep them
 *             yet; saveLayouts / saveDisplay send them (the server shapes,
 *             todoNav.js layoutsToServer / displayToServer)
 *   matchOf   for the filter on screen: { ids: Set, error } - what it shows
 *
 * The counts and the open filter's matches are the SERVER's answer (the query
 * is parsed and run there, over exactly what the list shows), asked again a
 * moment after the list itself changes - so ticking something off moves the
 * numbers beside the filters as it moves the ones beside the lists.
 */

/** The smart views a sidebar shows by default, in order, and the ones it keeps folded away. */
export const DEFAULT_VIEW_ORDER = ['today', 'upcoming', 'calendar', 'all', 'flagged', 'p1', 'waiting', 'overdue', 'scheduled', 'nodate',
  'done', 'assigned', 'delegated', 'notes', 'workload'];
export const DEFAULT_HIDDEN = ['overdue', 'scheduled', 'nodate'];

/**
 * The views there are (`views`, each { key, ... }) arranged by the person's
 * preferences: their order first, anything they have never arranged (a view
 * added since) in its default place, and hidden as they said - or as the
 * default says for one they never touched.
 */
export function arrangeViews(views, prefs) {
  const byKey = new Map(views.map((v) => [v.key, v]));
  const saved = (prefs || []).filter((p) => byKey.has(p.key));
  const out = saved.map((p) => ({ ...byKey.get(p.key), hidden: Boolean(p.hidden) }));
  const placed = new Set(out.map((v) => v.key));
  const order = [...DEFAULT_VIEW_ORDER, ...views.map((v) => v.key).filter((k) => !DEFAULT_VIEW_ORDER.includes(k))];
  order.forEach((k, i) => {
    if (placed.has(k) || !byKey.has(k)) return;
    // After the nearest view that comes before it by default and is placed.
    let at = 0;
    for (let j = i - 1; j >= 0; j -= 1) {
      const idx = out.findIndex((v) => v.key === order[j]);
      if (idx >= 0) { at = idx + 1; break; }
    }
    out.splice(at, 0, { ...byKey.get(k), hidden: DEFAULT_HIDDEN.includes(k) });
    placed.add(k);
  });
  return out;
}

export default function useTodoSmart({ on, api, open, filterId }) {
  const [filters, setFilters] = useState(null);
  const [fields, setFields] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [remote, setRemote] = useState(null);   // { layouts, display, synced }
  const [match, setMatch] = useState(null);     // { id, ids: Set, error, loading }
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);

  const loadFilters = useCallback(() => {
    if (!on) return Promise.resolve();
    return api.get('/filters')
      .then((r) => {
        if (!live.current) return;
        setFilters(Array.isArray(r.data?.filters) ? r.data.filters : []);
        setFields(Array.isArray(r.data?.fields) ? r.data.fields : []);
      })
      .catch(() => { if (live.current) setFilters((prev) => prev || []); });
  }, [on, api]);

  const loadMatch = useCallback((id) => {
    if (!on || !id) return Promise.resolve();
    setMatch((prev) => (prev && prev.id === id ? { ...prev, loading: true } : { id, ids: null, error: null, loading: true }));
    return api.get(`/filters/${id}/items`)
      .then((r) => {
        if (live.current) setMatch({ id, ids: new Set((r.data?.ids || []).map(String)), error: null, loading: false });
      })
      .catch((err) => {
        if (!live.current) return;
        const b = err.response?.data || {};
        setMatch({ id, ids: new Set(), loading: false,
                   error: err.response?.status === 404 ? { error: 'That filter is not there any more' }
                     : { error: b.error || 'Could not run that filter', position: b.position, length: b.length } });
      });
  }, [on, api]);

  // The preferences, once.
  useEffect(() => {
    if (!on) { setPrefs(null); setFilters(null); setRemote(null); return; }
    api.get('/view-prefs')
      .then((r) => {
        if (!live.current) return;
        const d = r.data || {};
        setPrefs(Array.isArray(d.views) ? d.views : []);
        // A server from before 26 Oct answers { views } alone and drops the
        // rest of a PUT: nothing to adopt, and nothing worth sending it.
        const synced = d.layouts !== undefined && d.display !== undefined;
        setRemote({ layouts: synced ? d.layouts || {} : {}, display: synced ? d.display || {} : {}, synced });
      })
      .catch(() => { if (live.current) { setPrefs([]); setRemote({ layouts: {}, display: {}, synced: false }); } });
  }, [on, api]);

  // The filters and their counts - and the open filter's rows - again a
  // moment after the list changes (a tick, an edit, a move all reload it).
  useEffect(() => {
    if (!on) return undefined;
    const t = setTimeout(() => {
      loadFilters();
      if (filterId) loadMatch(filterId);
    }, filters === null ? 0 : 250);
    return () => clearTimeout(t);
    // Asked again when the list is read again, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, open, filterId, loadFilters, loadMatch]);

  /* ---------------------------------------------------------- changes */

  const savePrefsTimer = useRef(null);
  /** The sidebar's views, rearranged: shown at once, sent a moment later (several clicks, one PUT). */
  const savePrefs = useCallback((views) => {
    const clean = views.map((v) => ({ key: v.key, hidden: Boolean(v.hidden) }));
    setPrefs(clean);
    clearTimeout(savePrefsTimer.current);
    savePrefsTimer.current = setTimeout(() => {
      api.put('/view-prefs', { views: clean }).catch(() => {});
    }, 400);
  }, [api]);
  useEffect(() => () => clearTimeout(savePrefsTimer.current), []);

  /*
   * Layouts and Display choices: sent at once - each is one deliberate click,
   * and somebody who switches to Calendar and closes the tab must find it on
   * their phone - one after another per field, so two quick changes cannot
   * land in the wrong order.
   */
  const syncQueue = useRef({});
  const saveRemote = useCallback((field, value) => {
    if (!on || !remote || !remote.synced) return;
    setRemote((r) => (r ? { ...r, [field]: value } : r));
    syncQueue.current[field] = (syncQueue.current[field] || Promise.resolve())
      .then(() => api.put('/view-prefs', { [field]: value }))
      .catch(() => {});
  }, [on, remote, api]);
  const saveLayouts = useCallback((v) => saveRemote('layouts', v), [saveRemote]);
  const saveDisplay = useCallback((v) => saveRemote('display', v), [saveRemote]);

  const create = useCallback(async (body) => {
    const r = await api.post('/filters', body);
    await loadFilters();
    return r.data;
  }, [api, loadFilters]);

  const update = useCallback(async (f, body) => {
    setFilters((prev) => (prev || []).map((x) => (x.id === f.id ? { ...x, ...body } : x)));
    try {
      const r = await api.patch(`/filters/${f.id}`, body);
      await loadFilters();
      if (filterId === f.id) loadMatch(f.id);
      return r.data;
    } catch (err) {
      loadFilters();
      throw err;
    }
  }, [api, loadFilters, loadMatch, filterId]);

  const remove = useCallback(async (f) => {
    setFilters((prev) => (prev || []).filter((x) => x.id !== f.id));
    try { await api.del(`/filters/${f.id}`); } finally { loadFilters(); }
  }, [api, loadFilters]);

  const restore = useCallback(async (f) => {
    await api.post(`/filters/${f.id}/restore`, {});
    await loadFilters();
  }, [api, loadFilters]);

  /** Drag or Move up/down: shown at once, the server renumbers. */
  const reorder = useCallback(async (from, to) => {
    const list = (filters || []).slice();
    const [moving] = list.splice(from, 1);
    if (!moving) return;
    list.splice(to, 0, moving);
    setFilters(list.map((x, i) => ({ ...x, position: i })));
    try { await api.patch(`/filters/${moving.id}`, { position: to }); } finally { loadFilters(); }
  }, [api, filters, loadFilters]);

  const preview = useCallback((query) => api.post('/filters/preview', { query }), [api]);

  const byId = useMemo(() => new Map((filters || []).map((f) => [f.id, f])), [filters]);

  return {
    on, filters, fields, prefs, remote, byId, match: match && match.id === filterId ? match : null,
    loadFilters, loadMatch, savePrefs, saveLayouts, saveDisplay, create, update, remove, restore, reorder, preview
  };
}
